import React, { useMemo } from 'react';
import type { EventRow } from '../api/events';
import type { ProjectRow } from '../api/projects';

interface Props {
  events: EventRow[];
  projects: ProjectRow[];
  projectId: number | null;
  eventId: number | null;
  onProjectIdChange: (projectId: number | null) => void;
  onEventIdChange: (eventId: number | null) => void;
  /** Fired when event or project association changes (e.g. reset dependency picker). */
  onAssociationChange?: () => void;
  projectSelectId?: string;
  eventSelectId?: string;
  disabled?: boolean;
}

/**
 * Event + project pickers for tasks. Project and event are independent; a task
 * may belong to both, either, or neither.
 */
const TaskEventProjectFields: React.FC<Props> = ({
  events,
  projects,
  projectId,
  eventId,
  onProjectIdChange,
  onEventIdChange,
  onAssociationChange,
  projectSelectId = 'task-project',
  eventSelectId = 'task-event',
  disabled = false,
}) => {
  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [events]
  );
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [projects]
  );

  const handleEventChange = (raw: string) => {
    const next = raw === '' ? null : parseInt(raw, 10);
    onEventIdChange(next != null && Number.isFinite(next) ? next : null);
    onAssociationChange?.();
  };

  const handleProjectChange = (raw: string) => {
    const next = raw === '' ? null : parseInt(raw, 10);
    onProjectIdChange(next != null && Number.isFinite(next) ? next : null);
    onAssociationChange?.();
  };

  return (
    <>
      <div className="form-group">
        <label htmlFor={projectSelectId} className="form-label">
          Project
        </label>
        <select
          id={projectSelectId}
          className="form-select"
          value={projectId === null ? '' : String(projectId)}
          onChange={(e) => handleProjectChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">— None (not on a project) —</option>
          {sortedProjects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.status === 'On Hold' ? ' (On Hold)' : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor={eventSelectId} className="form-label">
          Event
        </label>
        <select
          id={eventSelectId}
          className="form-select"
          value={eventId === null ? '' : String(eventId)}
          onChange={(e) => handleEventChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">— None (not on an event) —</option>
          {sortedEvents.map((ev) => (
            <option key={ev.id} value={ev.id}>
              {ev.name}
              {ev.status === 'Closed' ? ' (Void)' : ''}
            </option>
          ))}
        </select>
      </div>
    </>
  );
};

export default TaskEventProjectFields;
